import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageSquare, Building2, Users, FileText, Globe } from 'lucide-react';
export function ContactPage() {
  const contactReasons = [{
    icon: <Building2 size={24} data-id="element-183" />,
    title: 'Partnership Inquiries',
    description: 'Explore collaboration opportunities with educational institutions, employers, governments, or research organizations.'
  }, {
    icon: <Users size={24} data-id="element-184" />,
    title: 'Platform Demos',
    description: 'Request a demonstration of CJI, NCJI, or the broader SyNeural platform for your organization.'
  }, {
    icon: <FileText size={24} data-id="element-185" />,
    title: 'Research Collaboration',
    description: 'Discuss joint research initiatives, pilot programs, or academic partnerships.'
  }, {
    icon: <Globe size={24} data-id="element-186" />,
    title: 'Government & Public Sector',
    description: 'Inquire about civic resilience planning, civil service modernization, or leadership qualification frameworks.'
  }, {
    icon: <MessageSquare size={24} data-id="element-187" />,
    title: 'Media & Press',
    description: "Media inquiries, interview requests, or information about SyNeural's mission and platform."
  }, {
    icon: <Mail size={24} data-id="element-188" />,
    title: 'General Inquiries',
    description: 'Any other questions about SyNeural, our work, or how we might be able to help.'
  }];
  return <div className="min-h-screen bg-surface-bg" data-id="element-189">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-190">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-191">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-192">
            <span className="w-8 h-px bg-teal" data-id="element-193"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-194">
              Contact
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-195"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-196">
            Get in Touch
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-197">
            We're interested in hearing from organizations, institutions,
            researchers, and leaders who share our commitment to strengthening
            human capability, improving judgment quality, and building more
            resilient systems.
          </p>
        </motion.div>
      </section>

      {/* Contact Reasons */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-198">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-12 text-center" data-id="element-199">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-200">
            Reasons to Reach Out
          </h2>
          <p className="text-lg text-ink-muted max-w-2xl mx-auto" data-id="element-201">
            Whether you'We are exploring a partnership, requesting a demo, or
            have questions about our research – we'd like to hear from you.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-202">
          {contactReasons.map((reason, index) => <motion.div key={reason.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-white p-6 rounded-lg border border-line" data-id="element-203">
              <div className="w-12 h-12 rounded-lg bg-teal/10 flex items-center justify-center text-teal mb-4" data-id="element-204">
                {reason.icon}
              </div>
              <h3 className="text-lg font-heading font-bold text-ink-dark mb-2" data-id="element-205">
                {reason.title}
              </h3>
              <p className="text-sm text-ink-muted leading-relaxed" data-id="element-206">
                {reason.description}
              </p>
            </motion.div>)}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-207">
        <div className="max-w-3xl mx-auto" data-id="element-208">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-209">
            <div className="bg-white p-8 lg:p-12 rounded-xl border border-line shadow-sm" data-id="element-210">
              <h2 className="text-3xl font-heading font-bold text-ink-dark mb-6 text-center" data-id="element-211">
                Send Us a Message
              </h2>
              <p className="text-ink-muted text-center mb-10" data-id="element-212">
                Fill out the form below and we'll get back to you as soon as
                possible.
              </p>

              <form className="space-y-6" data-id="element-213">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-214">
                  <div data-id="element-215">
                    <label htmlFor="firstName" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-216">
                      First Name *
                    </label>
                    <input type="text" id="firstName" name="firstName" required className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" data-id="element-217" />
                  </div>
                  <div data-id="element-218">
                    <label htmlFor="lastName" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-219">
                      Last Name *
                    </label>
                    <input type="text" id="lastName" name="lastName" required className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" data-id="element-220" />
                  </div>
                </div>

                <div data-id="element-221">
                  <label htmlFor="email" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-222">
                    Email Address *
                  </label>
                  <input type="email" id="email" name="email" required className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" data-id="element-223" />
                </div>

                <div data-id="element-224">
                  <label htmlFor="organization" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-225">
                    Organization
                  </label>
                  <input type="text" id="organization" name="organization" className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" data-id="element-226" />
                </div>

                <div data-id="element-227">
                  <label htmlFor="inquiryType" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-228">
                    Inquiry Type *
                  </label>
                  <select id="inquiryType" name="inquiryType" required className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" data-id="element-229">
                    <option value="" data-id="element-230">Select an option</option>
                    <option value="partnership" data-id="element-231">Partnership Inquiry</option>
                    <option value="demo" data-id="element-232">Platform Demo Request</option>
                    <option value="research" data-id="element-233">Research Collaboration</option>
                    <option value="government" data-id="element-234">
                      Government & Public Sector
                    </option>
                    <option value="media" data-id="element-235">Media & Press</option>
                    <option value="general" data-id="element-236">General Inquiry</option>
                  </select>
                </div>

                <div data-id="element-237">
                  <label htmlFor="message" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-238">
                    Message *
                  </label>
                  <textarea id="message" name="message" rows={6} required className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent resize-none" placeholder="Tell us about your inquiry..." data-id="element-239"></textarea>
                </div>

                <button type="submit" className="w-full bg-navy hover:bg-navy-light text-white font-medium px-8 py-4 rounded-lg transition-colors shadow-sm" data-id="element-240">
                  Send Message
                </button>

                <p className="text-xs text-ink-muted text-center" data-id="element-241">
                  * Required fields
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Additional Contact Info */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-242">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-243">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-8 text-center" data-id="element-244">
            Other Ways to Connect
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-245">
            <div className="bg-surface-muted p-8 rounded-lg border border-line text-center" data-id="element-246">
              <div className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center text-teal mx-auto mb-4" data-id="element-247">
                <Mail size={28} data-id="element-248" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-2" data-id="element-249">
                Email Us Directly
              </h3>
              <p className="text-ink-muted mb-4" data-id="element-250">For general inquiries</p>
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-251">
                contact@syneural.com
              </a>
            </div>

            <div className="bg-surface-muted p-8 rounded-lg border border-line text-center" data-id="element-252">
              <div className="w-14 h-14 rounded-full bg-navy/10 flex items-center justify-center text-navy mx-auto mb-4" data-id="element-253">
                <Building2 size={28} data-id="element-254" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-2" data-id="element-255">
                Partnership Inquiries
              </h3>
              <p className="text-ink-muted mb-4" data-id="element-256">
                For institutional partnerships
              </p>
              <a href="mailto:partnerships@syneural.com" className="text-navy font-medium hover:underline" data-id="element-257">
                partnerships@syneural.com
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Response Time Notice */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-258">
        <div className="max-w-3xl mx-auto text-center" data-id="element-259">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-260">
            <h3 className="text-2xl font-heading font-bold mb-4" data-id="element-261">
              We'll Get Back to You Soon
            </h3>
            <p className="text-lg opacity-90" data-id="element-262">
              We typically respond to inquiries within 2-3 business days. For
              urgent matters, please indicate that in your message.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}